import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AllCommunityModule, ModuleRegistry, DateFilterModule, } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { toast, ToastContainer } from 'react-toastify';
import axiosInstance from '../../lib/axios';
import { Box, Typography, CircularProgress, Tooltip, IconButton, Button } from '@mui/material';
import { usePermissions } from '../../GlobalFunction/userPermissions';
import { useSelector } from 'react-redux';
import { selectGlobalProperty } from '../../redux/propertySlice';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import '../../AgGridStyle.css';
import HomeIcon from '@mui/icons-material/Home';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';

ModuleRegistry.registerModules([AllCommunityModule, DateFilterModule,]);

const ResidentAgeing = () => {
    const [rowData, setRowData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const UserId = sessionStorage?.getItem('UserId');
    const permissions = usePermissions("Resident Ageing");
    const { AddRec = "0" } = permissions || {};
    const gridRef = useRef();
    const navigate = useNavigate();
    const globalProperty = useSelector(selectGlobalProperty);

    const colDefs = useMemo(() => [
        {
            field: 'RowNum', headerName: 'Sr.No', width: 70,
            valueFormatter: (params) => params.node.rowPinned === 'bottom' ? 'Total' : params.value
        },
        { field: 'ResidentName', headerName: 'Resident', width: 180 },
        { field: 'MobileNo', headerName: 'Mobile No', width: 130 },
        { field: 'Col1', headerName: '0-30 Days', width: 130 },
        { field: 'Col2', headerName: '31-60 Days', width: 130 },
        { field: 'Col3', headerName: '61-90 Days', width: 130 },
        { field: 'Col4', headerName: '91-120 Days', width: 130 },
        { field: 'Col5', headerName: '121-150 Days', width: 130 },
        { field: 'Col6', headerName: '151-180 Days', width: 130 },
        { field: 'Col7', headerName: 'Above 180 Days', width: 130 },
        { field: 'TotalAmountSmry', headerName: 'Grand Total', width: 130 },
    ], []);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        filter: true,
        resizable: true,
        floatingFilter: true,
        width: 100,
        tooltipValueGetter: (params) => params.value ?? '',
    }), []);

    useEffect(() => {
        fetchResidentAgeing()
    }, [globalProperty]);

    const fetchResidentAgeing = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.post(`OutStandingReports/GetAgeingReports`, {
                PytAdvFrmDt: '1900-01-01',
                PytAdvToDt: '1900-01-01',
                PropId: globalProperty,
                ResidentId: "10241",
                Flag: "AgeingOs"
            });

            if (response.data.Status === 0) {
                const formattedData = response.data.Data.map(resident => ({
                    ...resident,
                    ResidentId: resident.ResidentId,
                }));
                setRowData(formattedData);
            } else {
                toast.info(response.data.Message);
            }
        } catch (error) {
            toast.error('Error fetching resident data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const pinnedBottomRowData = useMemo(() => {
        if (!rowData.length) return [];

        const totals = {
            RowNum: '',
            ResidentName: '',
            MobileNo: '',
            Col1: 0, Col2: 0, Col3: 0, Col4: 0,
            Col5: 0, Col6: 0, Col7: 0, TotalAmountSmry: 0,
        };

        rowData.forEach(row => {
            totals.Col1 += Number(row.Col1) || 0;
            totals.Col2 += Number(row.Col2) || 0;
            totals.Col3 += Number(row.Col3) || 0;
            totals.Col4 += Number(row.Col4) || 0;
            totals.Col5 += Number(row.Col5) || 0;
            totals.Col6 += Number(row.Col6) || 0;
            totals.Col7 += Number(row.Col7) || 0;
            totals.TotalAmountSmry += Number(row.TotalAmountSmry) || 0;
        });

        return [totals];
    }, [rowData]);

    const onGridReady = useCallback((params) => {
        // No sizeColumnsToFit here to preserve flex sizing
    }, []);

    const handleClearFilters = () => {
        gridRef.current?.api?.setFilterModel(null);
    };

    const handleExportToExcel = () => {
        if (!rowData || rowData.length === 0) {
            toast.info("No records to export");
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Resident Ageing');

        // ==================== Borders ====================
        const borderStyle = {
            top: { style: 'thin', color: { argb: 'FF81C4FF' } },
            left: { style: 'thin', color: { argb: 'FF81C4FF' } },
            bottom: { style: 'thin', color: { argb: 'FF81C4FF' } },
            right: { style: 'thin', color: { argb: 'FF81C4FF' } },
        };

        // ==================== TITLE ====================
        worksheet.mergeCells('A1:K1');
        const titleCell = worksheet.getCell('A1');
        titleCell.value = 'Resident Ageing Report';
        titleCell.font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
        titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '635BFF' } };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        titleCell.border = borderStyle;

        // ==================== HEADER ====================
        const headers = [
            'Sr.No', 'Resident', 'Mobile No', '0-30 Days', '31-60 Days', '61-90 Days',
            '91-120 Days', '121-150 Days', '151-180 Days', 'Above 180 Days', 'Grand Total'
        ];

        const headerRow = worksheet.addRow(headers);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '635BFF' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border = borderStyle;
        });
        headerRow.height = 22;

        // ==================== DATA ROWS ====================
        rowData.forEach((row, index) => {
            const dataRow = worksheet.addRow([
                row.RowNum,
                row.ResidentName,
                row.MobileNo,
                Number(row.Col1 || 0),
                Number(row.Col2 || 0),
                Number(row.Col3 || 0),
                Number(row.Col4 || 0),
                Number(row.Col5 || 0),
                Number(row.Col6 || 0),
                Number(row.Col7 || 0),
                Number(row.TotalAmountSmry || 0)
            ]);

            dataRow.eachCell((cell) => {
                if (index % 2 === 0) {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
                }
                cell.border = borderStyle;
            });

            // Align numbers to right
            for (let i = 4; i <= 11; i++) {
                dataRow.getCell(i).numFmt = '#,##0';
                dataRow.getCell(i).alignment = { horizontal: 'right' };
            }
        });

        // ==================== TOTAL ROW ====================
        const totals = {
            Col1: rowData.reduce((sum, r) => sum + Number(r.Col1 || 0), 0),
            Col2: rowData.reduce((sum, r) => sum + Number(r.Col2 || 0), 0),
            Col3: rowData.reduce((sum, r) => sum + Number(r.Col3 || 0), 0),
            Col4: rowData.reduce((sum, r) => sum + Number(r.Col4 || 0), 0),
            Col5: rowData.reduce((sum, r) => sum + Number(r.Col5 || 0), 0),
            Col6: rowData.reduce((sum, r) => sum + Number(r.Col6 || 0), 0),
            Col7: rowData.reduce((sum, r) => sum + Number(r.Col7 || 0), 0),
            TotalAmountSmry: rowData.reduce((sum, r) => sum + Number(r.TotalAmountSmry || 0), 0)
        };

        const totalRow = worksheet.addRow([
            'Total', '', '',
            totals.Col1, totals.Col2, totals.Col3,
            totals.Col4, totals.Col5, totals.Col6,
            totals.Col7, totals.TotalAmountSmry
        ]);

        totalRow.eachCell((cell, colNumber) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '635BFF' } };
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.border = borderStyle;
            if (colNumber >= 4) {
                cell.numFmt = '#,##0';
                cell.alignment = { horizontal: 'right' };
            }
        });

        totalRow.height = 22;

        // ==================== COLUMN WIDTHS ====================
        worksheet.columns = [
            { width: 8 },   // Sr.No
            { width: 20 },  // Resident
            { width: 15 },  // Mobile No
            { width: 12 },  // 0-30 Days
            { width: 12 },  // 31-60 Days
            { width: 12 },  // 61-90 Days
            { width: 12 },  // 91-120 Days
            { width: 12 },  // 121-150 Days
            { width: 12 },  // 151-180 Days
            { width: 14 },  // Above 180 Days
            { width: 14 }   // Grand Total
        ];

        // ==================== EXPORT ====================
        workbook.xlsx.writeBuffer().then((buffer) => {
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, `Resident_Ageing_Report.xlsx`);
        });
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '90vh',
                padding: '10px',
                backgroundColor: 'rgb(249, 249, 249)',
                position: 'relative'
            }}
        >
            <ToastContainer />
            <Box
                sx={{
                    backgroundColor: "#ffffff",
                    padding: { xs: "8px 16px", sm: "12px 16px" },
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                    borderRadius: "12px",
                    margin: "10px auto",
                    maxWidth: "95%",
                    flexDirection: { xs: "column", sm: "column", md: "row" },
                    gap: 2,
                }}
            >
                <Breadcrumbs
                    aria-label="breadcrumb"
                    separator="›"
                    sx={{
                        fontSize: { xs: "14px", sm: "16px" },
                        display: "flex",
                        flexDirection: { xs: "column", sm: "column", md: "row" }
                    }}
                >
                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center" }}
                        onClick={() => navigate('/dashboard')}
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        <Box component="span">Home</Box>
                    </Link>
                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500" }}
                        onClick={() => navigate('/reports/accountsFinance')}
                    >
                        Account & Finance
                    </Link>

                    <Typography color="text.primary" sx={{ fontWeight: "500" }}>
                        Resident Ageing
                    </Typography>
                </Breadcrumbs>

                {/* Buttons container */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: { xs: 1, sm: 1, md: 0 },
                        flexShrink: 0,
                    }}
                >
                    <Tooltip title='Clear all applied filter' arrow>
                        <Button
                            variant="outlined"
                            onClick={handleClearFilters}
                            size="small"
                            sx={{
                                borderRadius: "50px",
                                textTransform: "none",
                                borderColor: "#635bff",
                                color: "#635bff",
                                "&:hover": {
                                    borderColor: "#524ccc",
                                    backgroundColor: "rgba(99, 91, 255, 0.04)",
                                }
                            }}
                        >
                            Clear Filters
                        </Button>
                    </Tooltip>

                    <Tooltip title='Export to Excel' arrow>
                        <Button
                            variant='outlined'
                            onClick={handleExportToExcel}
                            sx={{
                                backgroundColor: '#217346',
                                color: '#ffffff',
                                borderRadius: "50px",
                                padding: "5px 15px",
                                textTransform: 'none',
                                '&:hover': {
                                    backgroundColor: '#1b5a33',
                                    color: '#ffffff',
                                },
                            }}
                        >
                            <ImportExportIcon /> Export
                        </Button>
                    </Tooltip>

                    <Tooltip title='Previous Page' arrow>
                        <Box sx={{
                            display: "flex", alignItems: "center", gap: 1,
                            justifyContent: "flex-start"
                        }}
                        >
                            <IconButton
                                onClick={() => navigate('/reports/accountsFinance')}
                                sx={{
                                    backgroundColor: '#635bff',
                                    borderRadius: '50%',
                                    width: 35,
                                    height: 35,
                                    '&:hover': {
                                        backgroundColor: '#635bff',
                                    },
                                    boxShadow: 3,
                                }}
                            >
                                <ArrowBackIcon sx={{ color: 'white' }} />
                            </IconButton>
                        </Box>
                    </Tooltip>
                </Box>
            </Box>

            <Box className="table-grid ag-theme-alpine"
                sx={{ height: 'calc(95% - 56px)', width: '95%', margin: '0 auto', position: 'relative' }}
            >
                {isLoading ? (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 10,
                            textAlign: 'center',
                        }}
                    >
                        <CircularProgress size="3rem" sx={{ color: '#635bff' }} />

                        <Typography sx={{ marginTop: 2, color: '#635bff', fontWeight: 'bold' }}>
                            Loading data...
                        </Typography>
                    </Box>
                ) : (
                    <AgGridReact
                        ref={gridRef}
                        rowData={rowData}
                        columnDefs={colDefs}
                        defaultColDef={defaultColDef}
                        pagination={true}
                        paginationPageSize={1000}
                        paginationPageSizeSelector={[100, 500, 1000, 5000]}
                        onGridReady={onGridReady}
                        enableCellTextSelection={true}
                        rowHeight={25}
                        headerHeight={30}
                        floatingFiltersHeight={28}
                        overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">No rows to show</span>'}
                        tooltipShowDelay={0}
                        getRowStyle={(params) => ({
                            backgroundColor: params.node.rowIndex % 2 === 0 ? '#f1f5fd' : '#ffffff'
                        })}
                        pinnedBottomRowData={pinnedBottomRowData}
                        getRowStyle={(params) => {
                            if (params.node.rowPinned === 'bottom') {
                                return {
                                    backgroundColor: '#c1bef8',
                                    color: 'black',
                                    fontWeight: 'bold',
                                    fontSize: '13px',
                                };
                            }
                        }}
                    />
                )}
            </Box>
            <Box sx={{
                position: 'absolute',
                bottom: 30,
                left: 70,
                fontSize: '14px',
                color: 'black',
            }}>
                Total Records: <b>{rowData.length}</b>
            </Box>
        </Box>
    );
};

export default ResidentAgeing;